<script lang="ts">
  import { chatOpen, settingsOpen, profileFormOpen, profileEditTarget, profiles, activeProfileId, activeProfile, switchProfile, defaultProfileId, setDefaultProfile, user } from '../lib/stores';
  import { signOutUser } from '../lib/firebase';
  import BetaTag from './BetaTag.svelte';
  import LangSwitch from './LangSwitch.svelte';
  import { demoActive } from '../lib/demoMode';
  import { lang, t, localName, localInitials, maybe } from '../lib/i18n';
  import type { Profile } from '../lib/phr';

  const ownerName = (u: { displayName?: string | null; email?: string | null } | null) =>
    (localName($lang, u?.displayName) || u?.email || $t('hdr.yourAccount'));
  const ownerInitials = (u: { displayName?: string | null; email?: string | null } | null) => {
    const n = (u?.displayName || '').trim();
    if (n) { const p = n.split(/\s+/); return localInitials($lang, p[0], p[1]); }
    return (u?.email?.[0] ?? '·').toUpperCase();
  };
  const logoSrc = `${import.meta.env.BASE_URL}brand/wellness.svg`;
  const pInit = (p: Profile | null) => (p ? localInitials($lang, p.firstName, p.lastName) : '·');
  // Top bar shows just the FIRST name; if it's longer than 8 chars, abbreviate to 6.
  const firstOnly = (p: Profile | null, u: { displayName?: string | null; email?: string | null } | null) => {
    let f = localName($lang, p?.firstName);
    if (!f) { const n = localName($lang, u?.displayName); f = n ? n.split(/\s+/)[0] : (u?.email?.split('@')[0] ?? $t('s.youCap')); }
    return f.length > 8 ? f.slice(0, 6) + '…' : f;
  };
  // Manager sub-label: just the first name (abbreviated to 6 if >8), no "Managed by:".
  const mgrShort = () => {
    const full = (localName($lang, $activeProfile?.ownerName) || ownerName($user) || '').trim();
    const f = full.split(/\s+/)[0] || full;
    return f.length > 8 ? f.slice(0, 6) + '…' : f;
  };

  let menuOpen = $state(false);
  const toggle = () => (menuOpen = !menuOpen);
  async function pick(id: string) { menuOpen = false; await switchProfile(id); }
  function toggleDefault(p: Profile) { setDefaultProfile($defaultProfileId === p.id ? null : p.id); }
  function addProfile() { menuOpen = false; profileEditTarget.set(null); profileFormOpen.set(true); }
  function editProfile(p: Profile) { menuOpen = false; profileEditTarget.set(p); profileFormOpen.set(true); }
  async function logout() { menuOpen = false; try { await signOutUser(); } catch (_) {} }
</script>

<svelte:window onclick={(e) => { if (menuOpen && !(e.target as HTMLElement).closest('.profile')) menuOpen = false; }} />

<header class="hdr">
  <img class="hdr-logo" src={logoSrc} alt="wellness" aria-hidden="true" />
  <div class="brand">
    <span class="wordmark" aria-label="empowered">empowered</span>
    <BetaTag />
    <button class="chat" class:on={$chatOpen} aria-label={$t('hdr.assistant')} aria-expanded={$chatOpen} title={$t('hdr.assistant')} onclick={() => chatOpen.update((v) => !v)}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M21 11.5a8.5 8.5 0 0 1-12.2 7.6L3 21l1.9-5.8A8.5 8.5 0 1 1 21 11.5z" />
      </svg>
    </button>
  </div>
  <span class="spacer"></span>

  <button class="gear" class:on={$settingsOpen} aria-label={$t('s.title')} aria-expanded={$settingsOpen} title={$t('s.title')} onclick={() => settingsOpen.update((v) => !v)}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  </button>

  {#if $demoActive}<span class="langsw"><LangSwitch compact /></span>{/if}

  <div class="profile">
    <button class="profbtn" class:on={menuOpen} aria-expanded={menuOpen} title={$t('hdr.account')} onclick={toggle}>
      <span class="ptext">
        <span class="oname">{firstOnly($activeProfile, $user)}</span>
        <span class="psub faint">{mgrShort()}</span>
      </span>
      <span class="avatar" aria-hidden="true">{$activeProfile ? pInit($activeProfile) : ownerInitials($user)}</span>
    </button>

    {#if menuOpen}
      <div class="menu" role="menu">
        <div class="mhead">
          <span class="mh-name">{ownerName($user)}</span>
          <span class="mh-sub faint">{$t('hdr.profilesUnder')}</span>
        </div>
        {#each $profiles as p (p.id)}
          <div class="prow" class:on={p.id === $activeProfileId}>
            <button class="badge" role="menuitem" onclick={() => pick(p.id)}>
              <span class="bav" aria-hidden="true">{pInit(p)}</span>
              <span class="binfo">
                <span class="bname">{localName($lang, `${p.firstName} ${p.lastName}`)}{#if p.shared}<span class="tag faint">{$t('hdr.shared')}</span>{/if}</span>
                <span class="bmeta faint">{p.relationship ? maybe($lang, 'rel.', p.relationship) : '—'}</span>
              </span>
            </button>
            <button class="star" class:on={p.id === $defaultProfileId} title={p.id === $defaultProfileId ? $t('hdr.defaultOn') : $t('hdr.default')} aria-label={$t('hdr.default')} aria-pressed={p.id === $defaultProfileId} onclick={() => toggleDefault(p)}>
              <svg viewBox="0 0 24 24" fill={p.id === $defaultProfileId ? 'currentColor' : 'none'} stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17.8 6.8 19.2l1-5.8L3.5 9.2l5.9-.9z" /></svg>
            </button>
            <button class="edit" title={$t('s.editProfile')} aria-label={$t('s.editProfile')} onclick={() => editProfile(p)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
            </button>
          </div>
        {/each}
        <button class="addp" role="menuitem" onclick={addProfile}>{$t('hdr.addProfile')}</button>
      </div>
    {/if}
  </div>

  <button class="logout" aria-label={$t('s.logout')} title={$t('s.logout')} onclick={logout}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
  </button>
</header>

<style>
  .langsw { display: inline-flex; margin-left: var(--space-2); }
  .hdr { position: relative; display: flex; align-items: center; gap: var(--space-3); padding: calc(var(--safe-top) + var(--space-3)) var(--space-4) var(--space-3); background: var(--bg); border-bottom: 1px solid var(--line); }
  /* Wellness mark, centered in the top bar (hidden on very narrow screens to avoid
     overlapping the brand / profile). */
  .hdr-logo { position: absolute; left: 50%; top: calc(50% + var(--safe-top) / 2); transform: translate(-50%, -50%); height: 30px; width: auto; pointer-events: none; }
  @media (max-width: 560px) { .hdr-logo { display: none; } }
  .brand { display: flex; align-items: center; gap: var(--space-2); }
  .wordmark { font-family: var(--font-brand); font-weight: 400; font-size: var(--fs-md); letter-spacing: 0.03em; background: var(--accent-grad-text); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent; }
  .chat, .gear, .logout { display: grid; place-items: center; width: 30px; height: 30px; border-radius: var(--radius-pill); color: var(--text-2); border: 1px solid var(--line); transition: color var(--ease), background var(--ease), border-color var(--ease); }
  .chat svg, .gear svg, .logout svg { width: 17px; height: 17px; }
  .chat:hover, .gear:hover { color: var(--text); background: var(--surface-2); }
  .logout:hover { color: var(--danger); background: var(--danger-soft); border-color: var(--danger); }
  .chat.on, .gear.on { color: var(--accent); background: var(--accent-soft); border-color: var(--accent-soft); }
  .spacer { flex: 1; }

  .profile { position: relative; }
  .profbtn { display: flex; align-items: center; gap: var(--space-2); padding: 3px 3px 3px var(--space-3); border-radius: var(--radius-pill); border: 1px solid var(--line); color: var(--text); }
  .profbtn:hover, .profbtn.on { border-color: var(--line-strong); background: var(--surface-2); }
  .ptext { display: flex; flex-direction: column; align-items: flex-end; line-height: 1.15; }
  .oname { font-weight: 700; font-size: var(--fs-sm); max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .psub { font-size: var(--fs-2xs); }
  .avatar { display: grid; place-items: center; width: 34px; height: 34px; border-radius: var(--radius-pill); background: var(--accent-soft); color: var(--accent); font-weight: 700; font-size: var(--fs-sm); border: 1px solid var(--line-strong); flex: 0 0 auto; }

  .menu { position: absolute; right: 0; top: calc(100% + 6px); min-width: 260px; z-index: var(--z-toast); display: flex; flex-direction: column; gap: 2px; padding: var(--space-2); background: var(--surface); border: 1px solid var(--line-strong); border-radius: var(--radius-md); box-shadow: 0 12px 40px -12px color-mix(in srgb, var(--bg) 90%, black); }
  .mhead { display: flex; flex-direction: column; padding: var(--space-1) var(--space-2) var(--space-2); border-bottom: 1px solid var(--line); margin-bottom: var(--space-1); }
  .mh-name { font-weight: 700; font-size: var(--fs-sm); }
  .mh-sub { font-size: var(--fs-2xs); }
  .prow { display: flex; align-items: stretch; gap: 2px; border-radius: var(--radius-sm); }
  .prow.on { background: var(--accent-soft); }
  .badge { flex: 1; display: flex; align-items: center; gap: var(--space-2); text-align: left; padding: var(--space-2); border-radius: var(--radius-sm); color: var(--text); min-width: 0; }
  .badge:hover { background: var(--surface-2); }
  .prow.on .badge:hover { background: transparent; }
  .bav { display: grid; place-items: center; width: 30px; height: 30px; border-radius: var(--radius-pill); background: var(--surface-3); color: var(--text-2); font-weight: 700; font-size: var(--fs-2xs); flex: 0 0 auto; }
  .prow.on .bav { background: var(--accent-grad); color: var(--on-accent); }
  .binfo { display: flex; flex-direction: column; min-width: 0; line-height: 1.2; }
  .bname { font-size: var(--fs-sm); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .bmeta { font-size: var(--fs-2xs); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-transform: capitalize; }
  .tag { text-transform: none; }
  .edit { flex: 0 0 auto; display: grid; place-items: center; width: 30px; border-radius: var(--radius-sm); color: var(--text-3); }
  .edit:hover { color: var(--accent); background: var(--surface-2); }
  .edit svg { width: 15px; height: 15px; }
  .star { flex: 0 0 auto; display: grid; place-items: center; width: 30px; border-radius: var(--radius-sm); color: var(--text-3); }
  .star:hover { color: var(--accent); background: var(--surface-2); }
  .star.on { color: var(--accent); }
  .star svg { width: 15px; height: 15px; }
  .addp { text-align: left; padding: var(--space-2); border-radius: var(--radius-sm); color: var(--accent); font-weight: 700; font-size: var(--fs-sm); margin-top: var(--space-1); }
  .addp:hover { background: var(--surface-2); }
</style>
